import { ServerStackIcon } from '@heroicons/react/24/outline'
import { isEmpty } from 'lodash'
import pMap from 'p-map'
import { ReactNode, useState } from 'react'
import { QueryObserverResult, useMutation, useQueries, UseQueryOptions } from 'react-query'
import { useHistory, useParams } from 'react-router-dom'

import ConfirmActionDialog from '@/components/TailwindUI/ConfirmDialog'
import ConfirmAction from '@/components/TailwindUI/ConfirmDialog'
import Skeleton from '@/components/TailwindUI/Skeleton'
import useFeatures from '@/components/useFeatures'
import { WORKSHOP_AGENTS } from '@/constants/featureToggles'
import { useNotification } from '@/hooks/useNotification'
import DataSourceWizard from '@/pages/SpaceAppBuilder/components/DataSourceWizard/DataSourceWizard'
import {
  App,
  AppInstanceDataSource,
  DatasourceWizardMode,
  FeatureStatus,
  WorkgridCatalogDatasource,
  WorkgridDatasource
} from '@/pages/SpaceAppBuilder/utilities/interfaces'
import {
  dataSourceV3CatalogQueryOptions,
  datasourceV3WithAppQueryOptions
} from '@/pages/SpaceAppBuilder/utilities/queries'

import DataSourceRow from './DataSource'

type DataSourceModal = {
  isOpen: boolean
  mode: DatasourceWizardMode
  datasourceKey: string
  datasourceId: string
  dataSource?: WorkgridDatasource
}

type Props = {
  appQueryResult: QueryObserverResult<App>
  readOnly?: boolean
  onModalOpened?: () => void
  isTemplate?: boolean
}

type DataSourceDeletionError = {
  status: number
  code: string
  name: string
  featureNames?: string[]
}

type DataSourceDeletionFailureResponse = {
  data: DataSourceDeletionError
}
type Actions = 'deactivating' | null
type ActionConfirmDialog = {
  action: Actions
  isOpen: boolean
  message: string
  subMessage: ReactNode | string
  cancelText: string
  confirmText: string
  loadingText: string
  disabled: boolean
  dataSource: WorkgridDatasource | undefined
}

export default function DataSources({ appQueryResult, readOnly = true, onModalOpened, isTemplate = false }: Props) {
  const [dataSourceModal, setDataSourceModal] = useState<DataSourceModal>({
    isOpen: false,
    mode: DatasourceWizardMode.CREATE,
    datasourceKey: '',
    datasourceId: ''
  })
  const history = useHistory()
  const { spaceId, appId } = useParams<{ spaceId: string; appId: string }>()
  const featureFlags = useFeatures()
  const agentsFlagOn = !!featureFlags[WORKSHOP_AGENTS]

  const [confirmDelete, setConfirmDelete] = useState<any>({ isOpen: false, hasFailed: false, error: {} })
  const [deletingDataSourceIds, setDeletingDataSourceIds] = useState<string[]>([])
  const [action, setAction] = useState<Actions>(null)
  const [confirmAction, setConfirmAction] = useState<ActionConfirmDialog>({
    action: null,
    isOpen: false,
    message: '',
    subMessage: '',
    cancelText: '',
    confirmText: '',
    loadingText: '',
    disabled: false,
    dataSource: undefined
  })
  const dataSources = appQueryResult?.data?.dataSources
  const features = appQueryResult?.data?.features
  const activeFeatures = features?.filter(feature => feature.status === FeatureStatus.ACTIVE)

  type WorkgridCatalogDatasourceQueries = UseQueryOptions<WorkgridCatalogDatasource>[]
  const dataSourcesQuery = useQueries<WorkgridCatalogDatasourceQueries>(
    dataSources?.map((ds: AppInstanceDataSource) => ({
      ...dataSourceV3CatalogQueryOptions({
        spaceId,
        datasourceId: ds.datasourceId || ds.key
      })
    })) || []
  )

  const workgridDataSources = dataSourcesQuery.reduce(
    (workgridDataSources: WorkgridCatalogDatasource[], dataSource) => {
      if (dataSource.data) {
        workgridDataSources.push(dataSource.data)
      }
      return workgridDataSources
    },
    []
  )

  const mutateDataSource = useMutation({})
  const deactivateMutation = useMutation({})

  const { notify } = useNotification()

  function openDataSourceWizard(dataSource?: WorkgridDatasource | undefined) {
    onModalOpened?.()
    if (dataSource) {
      setDataSourceModal({
        isOpen: true,
        mode: readOnly ? DatasourceWizardMode.PREVIEW : DatasourceWizardMode.EDIT,
        datasourceKey: dataSource.datasourceId || '',
        datasourceId: dataSource.id,
        dataSource: dataSource
      })
    } else {
      setDataSourceModal({
        isOpen: true,
        mode: DatasourceWizardMode.CREATE,
        datasourceKey: '',
        datasourceId: '',
        dataSource: undefined
      })
    }
  }

  function handleDelete(dataSourceId: string, datasourceInstanceId: string) {
    setDeletingDataSourceIds([...deletingDataSourceIds, confirmDelete.id])

    mutateDataSource.mutate(
      // @ts-expect-error
      [
        datasourceV3WithAppQueryOptions({
          spaceId,
          appId,
          dataSourceId,
          datasourceInstanceId
        }).queryKey[0],
        { method: 'DELETE' }
      ],
      {
        onSuccess: async () => {
          setConfirmDelete({ ...confirmDelete, isOpen: false })
          await appQueryResult.refetch()
          setDeletingDataSourceIds(deletingDataSourceIds.filter(id => id !== datasourceInstanceId))
        },
        onError: (error: DataSourceDeletionFailureResponse) => {
          setConfirmDelete({ ...confirmDelete, hasFailed: true, error: error.data })
          setDeletingDataSourceIds(deletingDataSourceIds.filter(id => id !== datasourceInstanceId))
        }
      }
    )
  }

  function handleEdit(dataSource: WorkgridDatasource) {
    history.push(`/space/${spaceId}/appbuilder/datasources/${dataSource.datasourceId}/instances/${dataSource.id}`)
  }

  async function deactivateFeatures() {
    try {
      await pMap(appQueryResult?.data?.features ?? [], feature =>
        deactivateMutation.mutateAsync(
          // @ts-expect-error
          [
            `/v1/appbuilder/spaces/${spaceId}/instances/${appQueryResult?.data?.id}/features/${feature.id}/deactivate`,
            {
              headers: {
                'Content-Type': 'application/json'
              }
            }
          ]
        )
      )

      await appQueryResult.refetch()

      setAction(null)
      setConfirmAction({ ...confirmAction, isOpen: false })
      notify({
        variant: 'success',
        title: activeFeatures && activeFeatures?.length > 1 ? 'Features Deactivated' : 'Feature Deactivated',
        body: (
          <div className="text-sm text-zinc-500">
            {activeFeatures && activeFeatures?.length > 1 ? (
              <span> Features have been successfully deactivated. You can now swap your datasource instance.</span>
            ) : (
              <span>Feature has been successfully deactivated. You can now swap your datasource instance.</span>
            )}
          </div>
        )
      })
      openDataSourceWizard(confirmAction.dataSource)
    } catch {
      notify({
        variant: 'error',
        title:
          activeFeatures && activeFeatures?.length > 1
            ? 'Failed to Deactivate Features'
            : 'Failed to Deactivate Feature',
        body: (
          <div className="text-sm text-zinc-500">
            {activeFeatures && activeFeatures?.length > 1 ? (
              <span> Features have not been deactivated.</span>
            ) : (
              <span>Feature has not been deactivated.</span>
            )}
          </div>
        )
      })
    }
  }

  function handleSwapDataSourceInstance(dataSource: WorkgridDatasource) {
    if (isEmpty(activeFeatures)) {
      openDataSourceWizard(dataSource)
    } else {
      setConfirmAction({
        action: 'deactivating',
        isOpen: true,
        cancelText: 'Keep active',
        confirmText: activeFeatures && activeFeatures?.length > 1 ? 'Deactivate features' : 'Deactivate feature',
        loadingText: 'Deactivating...',
        disabled: deactivateMutation?.isError,
        message: deactivateMutation?.isError
          ? 'Unable to deactivate features'
          : activeFeatures && activeFeatures?.length > 1
            ? 'Are you sure you want to swap this instance and deactivate features?'
            : 'Are you sure you want to swap this instance and deactivate the feature?',
        subMessage: deactivateMutation?.isError ? (
          <>Deactivate failed</>
        ) : (
          <span>
            All features will need to be reactivated with the new data source instance to make them available to the
            target audience.
          </span>
        ),
        dataSource
      })
    }
  }

  const noDatasources = !appQueryResult.isLoading && dataSources?.length === 0

  const renderDeletionFailure = (error: DataSourceDeletionError) => {
    const featureNames = error?.featureNames
    return (
      <div>
        <span>You cannot remove </span> <span className="font-bold">{confirmDelete.name} </span>{' '}
        {featureNames && (
          <>
            <span>from this {agentsFlagOn ? 'agent' : 'app'} because it is used by: </span>
            <ul className="list-inside list-disc pt-3 pl-5 text-left">
              {featureNames.map(feature => (
                <li key={feature}>{feature}</li>
              ))}
            </ul>
            <span className="mt-2 block">
              Before removing this instance, you need to swap or add a new data source.
            </span>
          </>
        )}
      </div>
    )
  }

  function handleAction() {
    setAction(confirmAction.action)
    if (confirmAction.action === 'deactivating') {
      deactivateFeatures()
    }
  }

  return (
    <>
      <ConfirmActionDialog
        isOpen={confirmAction?.isOpen}
        onConfirm={handleAction}
        onCancel={() => setConfirmAction({ ...confirmAction, isOpen: false })}
        message={confirmAction.message}
        subMessage={confirmAction.subMessage}
        variant="error"
        cancelText={confirmAction.cancelText}
        confirmText={confirmAction.confirmText}
        loadingText={confirmAction.loadingText}
        loading={action === confirmAction.action}
        disabled={false}
      />
      <ConfirmAction
        variant="error"
        isOpen={confirmDelete.isOpen}
        onConfirm={() => handleDelete(confirmDelete.key, confirmDelete.id)}
        onCancel={() => setConfirmDelete({ ...confirmDelete, isOpen: false })}
        message={confirmDelete.hasFailed ? 'Unable to Remove Data Source' : 'Remove Data Source'}
        subMessage={
          confirmDelete.hasFailed ? (
            renderDeletionFailure(confirmDelete.error)
          ) : (
            <div>
              <span>Are you sure you want to remove</span> <span className="font-bold">{confirmDelete.name}</span>{' '}
              <span>from this app?</span>
            </div>
          )
        }
        loading={mutateDataSource.isLoading}
        disabled={confirmDelete.hasFailed}
      />
      <div className="is-modal group relative">
        <DataSourceWizard
          showInstanceSelection={true}
          appDataSources={dataSources}
          datasourceKey={dataSourceModal.datasourceKey}
          isOpen={dataSourceModal.isOpen}
          mode={dataSourceModal.mode}
          onClose={() => {
            setDataSourceModal({
              ...dataSourceModal,
              isOpen: false
            })
            // This prevents the modal from having its data re-fetched while it is still closing
            setTimeout(() => appQueryResult.refetch(), 500)
          }}
        />
      </div>
      <div className="w-full bg-white px-4">
        <div className="flex h-10 items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold leading-6 text-zinc-900">Data sources</h1>
          </div>
          <div>
            {!readOnly && !noDatasources && (
              <button
                type="button"
                className="block px-3 py-1.5 text-center text-sm font-semibold leading-6 text-blue-600 hover:text-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                onClick={() => openDataSourceWizard()}
              >
                + Add data source
              </button>
            )}
          </div>
        </div>
        <hr className="mt-2" />
        {noDatasources ? (
          <div className="mt-4 flex items-center justify-center">
            <div className="mt-4 flex w-5/6 flex-col items-center justify-center space-y-6">
              <ServerStackIcon className="h-20 w-20 stroke-1 text-zinc-400" aria-hidden="true" />
              <p className="mt-4 pl-2 text-center text-base text-zinc-500">
                Connect to popular 3rd party systems to quickly integrate with your {agentsFlagOn ? 'agent' : 'app'}{' '}
                {agentsFlagOn ? 'capabilities' : 'features'}
              </p>
              {!readOnly && (
                <button
                  type="button"
                  className="rounded-md bg-white px-3.5 py-2.5 text-sm font-semibold text-zinc-900 shadow-sm ring-1 ring-inset ring-zinc-300 hover:bg-zinc-50"
                  onClick={() => openDataSourceWizard()}
                >
                  Add a data source
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="flow-root">
            <ul className="divide-y divide-zinc-100">
              {appQueryResult.isLoading || appQueryResult.isRefetching ? (
                <>
                  {[0, 1].map(r => (
                    <li
                      key={`datasource-loader-${r}`}
                      data-testid="loader"
                      className="pointer-events-none flex items-center justify-between p-4 hover:cursor-default hover:bg-zinc-50"
                    >
                      <Skeleton className="h-5 w-full pr-8" />
                    </li>
                  ))}
                </>
              ) : (
                dataSources &&
                dataSources?.map(dataSource => (
                  <DataSourceRow
                    key={dataSource.id}
                    readOnly={readOnly}
                    workgridDataSource={
                      workgridDataSources.find(
                        workgridDs => workgridDs?.id === dataSource.key || workgridDs?.id === dataSource.datasourceId
                      )!
                    }
                    dataSourceInstanceId={dataSource.id}
                    isTemplate={isTemplate}
                    isDeleting={deletingDataSourceIds.includes(dataSource.id)}
                    onEdit={handleEdit}
                    onDelete={dataSource => {
                      if (dataSource.id) {
                        setConfirmDelete({
                          isOpen: true,
                          key: dataSource.datasourceId,
                          name: workgridDataSources.find(
                            workgridDataSource => workgridDataSource?.id === dataSource.datasourceId
                          )?.name,
                          id: dataSource.id
                        })
                      }
                    }}
                    onSwapInstance={handleSwapDataSourceInstance}
                  />
                ))
              )}
            </ul>
          </div>
        )}
      </div>
    </>
  )
}
